import type { SchemeDisambiguationRequest } from '../scheme-reference/scheme-match';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subscription, timer } from 'rxjs';
import { retry, switchMap } from 'rxjs/operators';
import { GroupService } from './group.service';
import { PatientService } from './patient.service';
import { SocketService } from './socket.service';

@Injectable({
  providedIn: 'root'
})
export class AIService {
  private apiUrl = 'api'; // Replace with your API URL
  private socketSubs: Subscription[] = [];

  private audioProcessingStatusSubject = new BehaviorSubject<any>(null);
  private transcriptProcessingOutputSubject = new BehaviorSubject<any>(null);
  private aiProcessingStatusSubject = new BehaviorSubject<any>(null);

  audioStatus$ = this.audioProcessingStatusSubject.asObservable();
  transcriptOutput$ = this.transcriptProcessingOutputSubject.asObservable();
  aiStatus$ = this.aiProcessingStatusSubject.asObservable();

  isProcessing = new BehaviorSubject<boolean>(false);

  socketId: string = '';

  constructor(
    private http: HttpClient,
    private socketService: SocketService,
    private groupService: GroupService,
    private patientService: PatientService,
  ) {

    this.socketService.reconnected$.subscribe(() => {
      if (this.socketId) {
        // Re-subscribe to socket stream (this also calls joinAIProcessing)
        // This ensures listeners are re-bound after reconnection
        this.subscribeToSocketStream();
        // Recover missed notifications after reconnection
        this.recoverMissedNotifications();
      }
    });

  }

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('token');
    return new HttpHeaders().set('Authorization', `Bearer ${token}`);
  }

  uploadImage(formData: FormData) {
    const token = localStorage.getItem('token');
    return this.http.put(`${this.apiUrl}/ai-beta/${this.groupService.group.id}/image`, formData, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  sendDebugInformation(debugConfig: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post(`${this.apiUrl}/ai/debug`, debugConfig, { headers: { Authorization: `Bearer ${token}` } });
  }

  performUserPromptOnImage(prompt: string, imageUrl: string) {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/ai-beta/${this.groupService.group.id}/image/prompt`, { prompt, imageUrl }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  extractPatientDetailsFromImage(groupId: string, imageUrl: string) {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/ai/extract-admission-image-information/${groupId}`, { imageUrl }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  /** Which of several matching scheme options a scanned plan name means; `bestId` is an offered id or null. */
  disambiguateScheme(groupId: string, request: SchemeDisambiguationRequest) {
    const token = localStorage.getItem('token');
    return this.http.post<{ success: boolean; bestId: string | null }>(`${this.apiUrl}/ai/disambiguate-scheme/${groupId}`, request, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  extractNoteDetailsFromImage(groupId: string, imageUrl: string, isWardRound?: boolean) {
    const token = localStorage.getItem('token');
    return this.http.post<any>(`${this.apiUrl}/ai/extract-note-image-information/${groupId}`, { imageUrl, isWardRound }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  resetStatus() {
    this.socketId = '';
    this.audioProcessingStatusSubject.next(null);
    this.aiProcessingStatusSubject.next(null);
  }

  transcribeAudio(audioFile: File): Observable<string> {
    this.socketId = this.generateRandomSocketId(20);
    this.subscribeToSocketStream();
    const formData = new FormData();
    formData.append('audio', audioFile);
    formData.append('socketId', this.socketId);
    return this.http.post<string>(`${this.apiUrl}/transcribe`, formData, { headers: this.getAuthHeaders() });
  }

  cacheRecording(audioFile: File): Observable<any> {
    const formData = new FormData();
    formData.append('audio', audioFile);
    return this.http.post(`${this.apiUrl}/cache-recording`, formData, { headers: this.getAuthHeaders() });
  }

  getCachedRecordings(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/cached-recordings`, { headers: this.getAuthHeaders() });
  }

  getCachedRecording(recordingId: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/cached-recordings/${recordingId}`, { headers: this.getAuthHeaders() });
  }

  markRecordingAsProcessed(recordingId: string): Observable<any> {
    return this.http.put(`${this.apiUrl}/cached-recordings/${recordingId}/processed`, {}, { headers: this.getAuthHeaders() });
  }

  transcribeAudioUrl(audioUrl: string): Observable<any> {
    const relativePath = audioUrl.replace('http://localhost:3000', '');
    return this.http.post(`${this.apiUrl}/transcribe-url`, { audioUrl: relativePath }, { headers: this.getAuthHeaders() });
  }

  processTranscript(transcript: string, name: string, generateTaskList: boolean, generateLetterOfMotivation: boolean, generateLetterToColleague: boolean, headings?: string, basicOutput?: boolean, hasPatient?: boolean): Observable<any> {

    const groupId = this.groupService.group.id;
    const patientId = hasPatient ? this.patientService.patient.id : null;
    const requestData = { transcript, name, generateTaskList, generateLetterOfMotivation, generateLetterToColleague, headings, basicOutput, socketId: this.socketId, groupId, patientId };

    this.socketId = this.socketId || this.generateRandomSocketId(20);
    this.subscribeToSocketStream();

    return this.http.post(`${this.apiUrl}/generate-output`, requestData, { headers: this.getAuthHeaders() });
  }

  generateNotesFromAudio(audioFile: File | null, basicOutput: boolean, inpatientId?: string | null, isWardRound?: boolean, transcript?: string, jobId?: string, startTime?: string, finishTime?: string): Observable<any> {
    const groupId = this.groupService.group.id;
    const patientId = inpatientId || this.patientService.patient.id;
    this.socketId = this.generateRandomSocketId(20);
    this.subscribeToSocketStream();
    const formData = new FormData();
    if (audioFile) {
      formData.append('audio', audioFile);
    }
    formData.append('socketId', this.socketId);
    formData.append('groupId', groupId);
    formData.append('patientId', patientId);
    formData.append('processImmediate', 'true');
    formData.append('basicOutput', `${basicOutput}`);
    formData.append('isWardRound', `${isWardRound}`);
    if (transcript) {
      formData.append('transcript', transcript);
    }
    if (jobId) {
      formData.append('jobId', jobId);
    }
    if (startTime) {
      formData.append('startTime', startTime);
    }
    if (finishTime) {
      formData.append('finishTime', finishTime);
    }

    const post$ = this.http.post<string>(`${this.apiUrl}/transcribe`, formData, { headers: this.getAuthHeaders() });
    // Ward rounds often complete transcription quickly; give joinAIProcessing time to settle before the API
    // triggers fast Azure Function status emissions (mirrors server-side delay in GenerateWardRoundOutput).
    const source$ = isWardRound ? timer(250).pipe(switchMap(() => post$)) : post$;
    return source$.pipe(
      retry(1) // retry once on failure (2 attempts total)
    );
  }

  generateTranscriptFromAudioSegment(audioFile: File, inpatientId: string | null | undefined, isWardRound: boolean, socketId: string, jobId: string): Observable<any> {
    const groupId = this.groupService.group.id;
    const patientId = inpatientId || this.patientService.patient.id;
    //this.subscribeToSocketStream();
    const formData = new FormData();
    formData.append('audio', audioFile);
    formData.append('jobId', jobId);
    formData.append('socketId', socketId);
    formData.append('groupId', groupId);
    formData.append('patientId', patientId);
    formData.append('isWardRound', `${isWardRound}`);

    return this.http.post<string>(`${this.apiUrl}/transcribe-segment`, formData, { headers: this.getAuthHeaders() });
  }

  getRequestDetails(requestId: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/ai-requests/${requestId}`, { headers: this.getAuthHeaders() });
  }

  // Drive aiStatus$ from a polling fallback when the `ai-request-completed`
  // socket emit is missed. Callers detect persisted completion via
  // PatientAiService.getAIRequestStatus(id) and pass the id here; downstream
  // subscribers run the same code path as a real completion event.
  emitSyntheticCompletion(aiRequestId: string): void {
    this.aiProcessingStatusSubject.next({
      completed: true,
      aiRequestId,
      request: { id: aiRequestId },
      message: 'Processing completed successfully.',
      error: false,
    });
  }

  getRequests(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/ai-requests/group/${this.groupService.group.id}`, { headers: this.getAuthHeaders() });
  }

  deleteRequest(requestId: string): Observable<any> {
    return of([]);
    //this.http.delete(`${this.apiUrl}/ai-requests/${requestId}`, { headers: this.getAuthHeaders() });
  }

  deletePendingRecording(recordingId: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/cached-recordings/${recordingId}`, { headers: this.getAuthHeaders() });
  }

  updateRequestName(requestId: string, name: string): Observable<any> {
    return this.http.put(`${this.apiUrl}/ai-requests/${requestId}/name`, { name }, { headers: this.getAuthHeaders() });
  }

  updateRequestNotes(requestId: string, notes: string): Observable<any> {
    return this.http.put(`${this.apiUrl}/ai-requests/${requestId}/notes`, { notes }, { headers: this.getAuthHeaders() });
  }

  updateRequestTaskList(requestId: string, taskList: string): Observable<any> {
    return this.http.put(`${this.apiUrl}/ai-requests/${requestId}/task-list`, { taskList }, { headers: this.getAuthHeaders() });
  }

  updateRequestLetterOfMotivation(requestId: string, letterOfMotivation: string): Observable<any> {
    return this.http.put(`${this.apiUrl}/ai-requests/${requestId}/letter-of-motivation`, { letterOfMotivation }, { headers: this.getAuthHeaders() });
  }

  updateRequestLetterToColleague(requestId: string, letterToColleague: string): Observable<any> {
    return this.http.put(`${this.apiUrl}/ai-requests/${requestId}/letter-to-colleague`, { letterToColleague }, { headers: this.getAuthHeaders() });
  }

  updateRequestRadiologyRequest(requestId: string, radiologyRequest: string): Observable<any> {
    return this.http.put(`${this.apiUrl}/ai-requests/${requestId}/radiology-request`, { radiologyRequest }, { headers: this.getAuthHeaders() });
  }

  private unsubscribeFromSocketStream() {
    for (const sub of this.socketSubs) {
      sub.unsubscribe();
    }
    this.socketSubs = [];
  }

  /*   private subscribeToSocketStream() {
  
      this.unsubscribeFromSocketStream();
  
      this.socketService.joinAIProcessingRoom(this.socketId);
  
      const subs = this.socketService.subscribeToAIProcessingEvents(this.socketId);
  
      this.socketSubs.push(
        subs[0].subscribe(output => {
          this.audioProcessingStatusSubject.next(output);
        }),
        subs[1].subscribe(output => {
          this.transcriptProcessingOutputSubject.next(output);
        }),
        subs[2].subscribe(output => {
          this.aiProcessingStatusSubject.next(output);
        })
      );
    } */

  private subscribeToSocketStream() {
    if (this.socketSubs) {
      for (const sub of this.socketSubs)
        sub.unsubscribe();
    }

    console.log('joinAIProcessing', { socketId: this.socketId });
    this.socketService.emit('joinAIProcessing', { socketId: this.socketId });
    console.log('joinAIProcessing emitted');

    this.socketSubs.push(
      this.socketService.listen('audioProcessingStatus', 'ai-service')
        .subscribe(output => {
          console.log('GOT AUDIO PROCESSING STATUS', output);
          this.audioProcessingStatusSubject.next(output);
        })
    );


    this.socketSubs.push(
      this.socketService.listen('transcriptionUpdated', 'ai-service')
        .subscribe(output => {
          console.log('GOT TRANSCRIPT PROCESSING OUTPUT', output);
          this.transcriptProcessingOutputSubject.next(output);
        })
    );


    this.socketSubs.push(
      this.socketService.listen('aiGenerationStatus', 'ai-service')
        .subscribe(output => {
          console.log('GOT AI PROCESSING STATUS', output);
          this.aiProcessingStatusSubject.next(output);
          if (output.completed) {
            this.isProcessing.next(false);
          }
        })
    );
  }

  /**
   * Recover missed notifications after socket reconnection
   * This ensures users don't lose AI generation status updates if they disconnect
   */
  recoverMissedNotifications(): void {
    if (!this.socketId) {
      return;
    }

    this.http.get<any>(`${this.apiUrl}/notifications/socket/${this.socketId}`, { headers: this.getAuthHeaders() })
      .subscribe({
        next: (response) => {
          if (response.success && response.data && response.data.length > 0) {
            console.log(`Recovering ${response.data.length} missed notifications for socketId: ${this.socketId}`);

            // Replay notifications in order (oldest first)
            response.data.forEach((notification: any) => {
              // Process through the same observable stream as live notifications
              // This ensures the UI updates correctly
              if (notification.completed) {
                // For completed notifications, emit to aiStatus$ to update the UI
                this.aiProcessingStatusSubject.next(notification);
                this.isProcessing.next(false);
              } else {
                // For in-progress notifications, also emit to aiStatus$
                this.aiProcessingStatusSubject.next(notification);
              }
            });
          }
        },
        error: (error) => {
          console.warn('Failed to recover missed notifications:', error);
          // Don't throw - recovery failure shouldn't break the app
        }
      });
  }

  generateRandomSocketId(length: number): string {
    const timestamp = new Date().getTime().toString();
    let result = '';
    const characters = 'abcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    for (let i = 0; i < length - timestamp.length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return (result + timestamp).toLowerCase().replace(/[^a-z0-9-]/g, '').substring(0, 63);;
  }

}